import type {
	OptimizationGroupModel,
	ScenarioMainCopySettings,
} from '@/shared/generated'

import React, { useMemo, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import {
	filter,
	fromPairs,
	indexBy,
	negate,
	pickAll,
	pipe,
	prop,
	propEq,
} from 'lodash/fp'
import {
	AlertTriangleIcon,
	CheckCircle2Icon,
	ChevronLeftIcon,
	ChevronRightIcon,
	InfoIcon,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import { useCopyMainScenarioSettingsMutation } from '@/entities/scenario'

import { ErrorBoundaryWithUser } from '@/shared/components/ErrorBoundary'
import { FormProvider, RHFMultiSelect } from '@/shared/components/HookForm'
import { Alert } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox-native'
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import {
	Field,
	FieldGroup,
	FieldLabel,
	FieldSeparator,
	FieldSet,
} from '@/shared/components/ui/field'
import { FormField } from '@/shared/components/ui/form'
import {
	Modal,
	ModalBody,
	ModalClose,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { ampli } from '@/shared/lib/ampli'
import { useModals } from '@/shared/lib/modal-manager'
import Sentry from '@/shared/lib/sentry'

type CopySettingsModalProps = {
	isOpen: boolean
	close(): void
	optimizationGroupId: string
}

const FormDataSchema = z.object({
	settings: z.array(z.string()).min(1, 'Please select at least one setting'),
	groups: z
		.array(z.string())
		.min(1, 'Please select at least one optimization group'),
})
type FormDataType = z.infer<typeof FormDataSchema>

type ContextValue = {
	step: 'form' | 'confirmation' | 'processing'
	setStep: (step: 'form' | 'confirmation' | 'processing') => void
	draft: FormDataType
	setDraft: (data: FormDataType) => void
	optimizationGroups: OptimizationGroupModel[]
	submit: () => void
	copySettingsMutation: ReturnType<typeof useCopyMainScenarioSettingsMutation>
}
export function CopyScenarioSettingsModal({
	isOpen,
	close,
	optimizationGroupId,
}: CopySettingsModalProps) {
	const { t } = useTranslation()

	const queryClient = useQueryClient()
	const { showModal } = useModals()
	const [draft, setDraft] = useState<FormDataType>({
		settings: [],
		groups: [],
	})
	const [step, setStep] = useState<'form' | 'confirmation' | 'processing'>(
		'form'
	)
	const groupsQuery = useOptimizationGroupsQuery<OptimizationGroupModel[]>({
		select: pipe([filter(negate(propEq('id', optimizationGroupId)))]),
	})
	const copySettingsMutation = useCopyMainScenarioSettingsMutation(
		optimizationGroupId,
		{
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ['optimizations'],
				})
				queryClient.invalidateQueries({
					queryKey: ['settings'],
				})
			},
			onError: (error) => {
				Sentry.captureException(error)
			},
		}
	)
	const onApplyFormData = async () => {
		const isConfirmed = await askAffectedOptimizationGroups(draft.groups)
		if (!isConfirmed) return
		setStep('processing')
		copySettingsMutation.mutateAsync({
			...getSettings(draft),
			dst_optimization_group_ids: draft.groups,
		})
	}
	const askAffectedOptimizationGroups = (optimizationGroupsIds: string[]) =>
		new Promise((resolve) => {
			showModal('AFFECTED_OPTIMIZATION_GROUPS_MODAL', {
				title: t('og.affect.modal.title', {
					defaultValue: 'Affected optimization groups',
				}),
				subtitle: t('og.affect.modal.subtitle', {
					defaultValue:
						'Your action will affect the following optimization groups:',
				}),
				getOptimizationGroupsIds: () => optimizationGroupsIds,
				onConfirm: () => resolve(true),
			})
		})
	const context = useMemo(
		() => ({
			step,
			setStep,
			draft,
			setDraft,
			optimizationGroups: groupsQuery.data || [],
			submit: onApplyFormData,
			copySettingsMutation,
		}),
		[step, draft, groupsQuery.data, copySettingsMutation]
	)

	return (
		<Modal
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) {
					close()
				}
			}}
		>
			<ModalContent className='max-w-xl'>
				<ModalHeader>
					<ModalTitle>{t('cp_og_settings_modal_title')}</ModalTitle>
				</ModalHeader>
				<ErrorBoundaryWithUser showDialog>
					{step === 'form' && (
						<CopyOptimizationGroupSettingsForm context={context} />
					)}
					{step === 'confirmation' && (
						<CopyOptimizationGroupSettingsConfirmation context={context} />
					)}
					{step === 'processing' && (
						<CopyOptimizationGroupSettingsProcessing context={context} />
					)}
				</ErrorBoundaryWithUser>
			</ModalContent>
		</Modal>
	)
}
const CopyOptimizationGroupSettingsConfirmation = ({
	context,
}: {
	context: ContextValue
}) => {
	const { t } = useTranslation()

	const groupsMap = indexBy<OptimizationGroupModel>(
		'id',
		context.optimizationGroups
	)

	return (
		<>
			<ModalBody className='flex flex-col gap-6'>
				<div className='flex flex-col gap-4'>
					<div className='flex flex-col gap-3'>
						<h5>{t('cp_og_settings_confirmation_settings')}</h5>
						<ul className='max-h-64 list-disc gap-3 overflow-y-auto pl-5 text-sm'>
							{context.draft.settings.map((key: string) => (
								<li key={key}>
									<p>{t(`setting_${key}`)}</p>
								</li>
							))}
						</ul>
					</div>
					<div className='flex flex-col gap-3'>
						<h5>
							{t('cp_og_settings_confirmation_ogs')}(
							{context.draft.groups.length}
							):
						</h5>
						<ul className='max-h-64 list-disc gap-2 overflow-y-auto pl-5 text-sm'>
							{context.draft.groups.map((id: string) => (
								<li key={id}>{groupsMap[id]?.name || 'None'}</li>
							))}
						</ul>
					</div>
					<div className='col-span-2'>
						<Alert variant='warning'>
							<InfoIcon />
							{t('cp_og_settings_confirmation_hint')}
						</Alert>
					</div>
				</div>
			</ModalBody>
			<ModalFooter>
				<Button
					onClick={() => context.setStep('form')}
					data-testid='copy-og-settings-modal-back'
				>
					<ChevronLeftIcon />
					{t('general_back')}
				</Button>
				<Button
					variant='primary'
					onClick={() => {
						ampli.settingsCopySettingsConfirm()
						context.submit()
					}}
					data-testid='copy-og-settings-modal-next'
				>
					{t('general_apply')}
				</Button>
			</ModalFooter>
		</>
	)
}

function CopyOptimizationGroupSettingsProcessing({
	context,
}: {
	context: ContextValue
}) {
	const { t } = useTranslation()

	const result = context.copySettingsMutation.data || []
	const success = result.filter(({ success }) => success)?.length | 0
	const failed = result.filter(({ success }) => !success).length | 0
	const total = context.draft.groups.length || 0
	if (context.copySettingsMutation.isPending) {
		return (
			<ModalBody className='flex min-h-72 flex-col gap-6'>
				<Empty className='h-full'>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<Spinner className='text-muted-foreground' />
						</EmptyMedia>

						<EmptyDescription>
							{t('cp_og_settings_updating_count', {
								count: success + failed,
								total,
							})}
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			</ModalBody>
		)
	} else if (failed > 0) {
		return (
			<ModalBody className='flex min-h-72 flex-col gap-6'>
				<Empty className='h-full'>
					<EmptyHeader>
						<EmptyMedia variant='icon' className='bg-orange-500/20'>
							<AlertTriangleIcon className='text-orange-500' />
						</EmptyMedia>
						<EmptyDescription>
							{t('cp_og_settings_result_with_errors', { failed, success })}
						</EmptyDescription>
					</EmptyHeader>
					<EmptyContent className='flex flex-row justify-center gap-2'>
						<Button
							variant='secondary'
							onClick={() => context.setStep('form')}
							data-testid='copy-og-settings-modal-back'
						>
							<ChevronLeftIcon />
							{t('general_back')}
						</Button>
						<Button
							variant='primary'
							onClick={() => {
								const groups = result
									.filter(({ success }) => !success)
									.map(prop('optimization_group_id')) as unknown as string[]
								context.copySettingsMutation.mutate({
									...getSettings(context.draft),
									dst_optimization_group_ids: groups,
								})
							}}
						>
							{t('cp_og_settings_retry')}
						</Button>
					</EmptyContent>
				</Empty>
			</ModalBody>
		)
	} else if (success > 0) {
		return (
			<ModalBody className='flex min-h-72 flex-col gap-6'>
				<Empty className='h-full'>
					<EmptyHeader>
						<EmptyMedia variant='icon' className='bg-green-500/20'>
							<CheckCircle2Icon className='text-green-500' />
						</EmptyMedia>
						<EmptyTitle>
							{t('app.success', { defaultValue: 'Success' })}
						</EmptyTitle>
						<EmptyDescription>
							{t('cp_og_settings_result_done')}
						</EmptyDescription>
					</EmptyHeader>
					<EmptyContent>
						<ModalClose asChild>
							<Button
								variant='primary'
								data-testid='copy-og-settings-modal-finish'
								onClick={() => ampli.settingsCopySettingsFinish()}
							>
								{t('general_close_and_finish')}
							</Button>
						</ModalClose>
					</EmptyContent>
				</Empty>
			</ModalBody>
		)
	} else {
		return (
			<>
				<ModalBody className='flex min-h-72 flex-col gap-6'>
					<Empty className='h-full'>
						<EmptyHeader>
							<EmptyMedia variant='icon' className='bg-red-500/20'>
								<AlertTriangleIcon className='text-red-500' />
							</EmptyMedia>
							<EmptyTitle>{t('fatal_error_title')}</EmptyTitle>
							<EmptyDescription>{t('fatal_error_desc')}</EmptyDescription>
						</EmptyHeader>
					</Empty>
				</ModalBody>
				<ModalFooter>
					<Button
						onClick={() => context.setStep('form')}
						data-testid='copy-og-settings-modal-back'
					>
						<ChevronLeftIcon />
						{t('general_back')}
					</Button>
					<ModalClose asChild>
						<Button
							variant='primary'
							data-testid='copy-og-settings-modal-finish'
							onClick={() => ampli.settingsCopySettingsFinish()}
						>
							{t('general_close_and_finish')}
						</Button>
					</ModalClose>
				</ModalFooter>
			</>
		)
	}
}

const CopyOptimizationGroupSettingsForm = ({
	context,
}: {
	context: ContextValue
}) => {
	const { t } = useTranslation()

	const methods = useForm<FormDataType>({
		defaultValues: context.draft,
		resolver: zodResolver(FormDataSchema),
	})
	const groupsOptions = context.optimizationGroups.map((g) => ({
		value: g.id,
		label: g.name,
	}))
	const settingsItems = settingsKeys?.map((item) => ({
		id: item,
		label: t(`setting_${item}`),
	}))

	return (
		<FormProvider
			methods={methods}
			className='flex flex-col gap-4'
			onSubmit={methods.handleSubmit((data) => {
				context.setDraft(data)
				context.setStep('confirmation')
			})}
		>
			<ModalBody className='flex flex-col gap-6'>
				<FieldSet>
					<FieldGroup className='mt-4'>
						{settingsItems.map((item) => (
							<FormField
								key={item.id}
								control={methods.control}
								name='settings'
								render={({ field }) => (
									<Field key={item.id} orientation='horizontal'>
										<Checkbox
											id={`s-${item.id}`}
											checked={field.value?.includes(item.id)}
											onChange={({ target: { checked } }) =>
												checked
													? field.onChange([...field.value, item.id])
													: field.onChange(
															field.value?.filter((value) => value !== item.id)
														)
											}
										/>
										<FieldLabel htmlFor={`s-${item.id}`}>
											{item.label}
										</FieldLabel>
									</Field>
								)}
							/>
						))}
					</FieldGroup>
					<FieldSeparator />
					<FieldGroup>
						<RHFMultiSelect
							name='groups'
							label={t('optimization_groups', {
								defaultValue: 'Optimization groups',
							})}
							options={groupsOptions || []}
						/>
					</FieldGroup>
				</FieldSet>
			</ModalBody>
			<ModalFooter>
				<ModalClose asChild>
					<Button
						className='mr-auto'
						data-testid='copy-og-settings-modal-cancel'
					>
						{t('general_cancel')}
					</Button>
				</ModalClose>
				<Button
					variant='primary'
					type='submit'
					data-testid='copy-og-settings-modal-next'
					onClick={() => {
						ampli.settingsCopySettingsSelectSettings({
							'Settings to copy': methods.watch('settings').join(' / '),
						})
					}}
				>
					{t('general_next')}
					<ChevronRightIcon />
				</Button>
			</ModalFooter>
		</FormProvider>
	)
}

const settingsKeys = [
	'copy_stop_list_settings',
	'copy_business_settings',
	'copy_strategy_settings',
]

const keysToObject = (keys: string[], flag: boolean) =>
	fromPairs(keys.map((key) => [key, flag]))

const getSettings = (formData: FormDataType) =>
	pickAll(settingsKeys, {
		...keysToObject(settingsKeys, false),
		...keysToObject(formData.settings, true),
	}) as unknown as Omit<ScenarioMainCopySettings, 'dst_optimization_group_ids'>
